// External library imports
import React, { memo, useMemo, FC } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';

// Internal library imports
import { Label, Text, Types } from '@marriott/mi-ui-library';

// Local imports
import { PointsCard } from '../../molecules/PointsCard';
import { VariationType } from '../../molecules/PointsCard/PointsCard.types';
import { StyledPointsEarned } from './PointsEarned.styles';
import { PointsEarnedProps, PointsListItem } from './PointsEarned.types';
import { shouldCardDisplay } from '../../modules/utils';
import { useStore } from '../../modules/store/cobrandStore';
import { COBRAND_CARD_TYPE } from '../../modules/constants/constants';

export const PointsEarnedConfig = {
  emptyLabel: 'PointsSection',
  isEmpty: () => true,
  resourceType: `/components/content/PointsSection`,
};

/**
 * PointsEarnedComp - Organism component for displaying points earning information
 *
 * Features:
 * - Memoized for performance optimization
 * - Supports detailed points breakdown
 * - Responsive layout
 * - Dynamic visibility based on card type
 * - Accessible with screen reader support
 *
 * Performance optimizations:
 * - React.memo prevents unnecessary re-renders
 * - useMemo for computed values
 * - Early returns for empty data
 *
 * @param props - PointsEarnedProps
 */
export const PointsEarnedComp: FC<PointsEarnedProps> = memo<PointsEarnedProps>(props => {
  const {
    noOfPoints,
    pointsDescription,
    pointDetailsTitle,
    pointsList = [],
    enablePointDetailsSection,
    cardName,
    cardType,
    customClass = '',
    column,
    variationType = VariationType.Default,
    styleClass = '',
    bonusLabel,
    isBonusPointsLabelVisible = false,
  } = props;

  // Get cobrand store data
  const { cobrandData } = useStore(store => store);

  // Memoize computed values
  const hasDetailedList = useMemo(() => enablePointDetailsSection === 'true', [enablePointDetailsSection]);

  const isCardVisible = useMemo(
    () => shouldCardDisplay(cardType, cobrandData.activeTabValue ?? ''),
    [cardType, cobrandData.activeTabValue]
  );

  const containerStyle = useMemo(() => {
    const isBusinessActive = cobrandData.activeTabValue === 'business' && isCardVisible;
    return isBusinessActive ? { minHeight: 0 } : {};
  }, [cobrandData.activeTabValue, isCardVisible]);

  const rowClasses = useMemo(
    () =>
      clsx(
        'row',
        variationType === VariationType.Default
          ? 'justify-content-center'
          : 'justify-content-center justify-content-lg-start'
      ),
    [variationType]
  );

  const columnClasses = useMemo(
    () =>
      clsx(
        column === '2' ? 'col-12 col-lg-10' : 'col-12',
        'points-earned px-2',
        variationType === VariationType.Default ? 'col-lg-8 col-md-10 col-12 px-md-0 px-5' : ''
      ),
    [column, variationType]
  );

  const listContainerClasses = useMemo(
    () =>
      clsx(
        column === '2' ? 'px-lg-5 pt-lg-4 pb-lg-2' : 'pb-lg-0',
        'points-earned-property d-flex flex-column pt-2 pr-2 pt-md-3 px-md-3 pb-0 pl-2',
        `list-${customClass}`
      ),
    [column, customClass]
  );

  // Early return if no points data
  if (!noOfPoints || !pointsDescription) {
    return null;
  }

  return (
    <StyledPointsEarned
      data-component-name="o-cobrand-PointsEarned"
      data-testid="cobrand-PointsEarned"
      className={clsx(customClass, styleClass, 'd-md-block', isCardVisible ? 'd-block' : 'd-none')}
      data-cardtype={cardType}
      role="region"
      aria-label={cardName ? `Points section for ${cardName}` : 'Points section'}
    >
      {/* Screen reader only card name */}
      {cardName && <span className="sr-only">{cardName}</span>}

      <div className={rowClasses}>
        <div className={columnClasses}>
          {/* Main points card */}
          <PointsCard
            isOnSpend={!hasDetailedList}
            number={noOfPoints}
            content={pointsDescription}
            column={column}
            cardType={cardType}
            customClass={customClass}
            variationType={variationType}
            bonusLabel={bonusLabel}
            isBonusPointsLabelVisible={isBonusPointsLabelVisible}
          />

          {/* Detailed points breakdown list */}
          {hasDetailedList && pointsList.length > 0 && (
            <div className={listContainerClasses} style={containerStyle}>
              {pointDetailsTitle && (
                <Label
                  labelText={pointDetailsTitle}
                  size={Types.size.extraSmall}
                  variation={Types.TypographyColorVariations.Alternate}
                  customClass={clsx('mb-lg-3 mb-2 mb-md-3')}
                />
              )}

              {pointsList.map((item: PointsListItem, index: number) => {
                const isLastItem = index === pointsList.length - 1;

                return (
                  <div
                    key={`${item.pointDetailsNoOfPoints}-${index}`}
                    className={clsx(
                      'd-flex pb-1 pb-md-0 mb-md-3',
                      cardType === COBRAND_CARD_TYPE.BUSINESS ? 'flex-row' : 'flex-column flex-md-row',
                      isLastItem ? 'mb-1' : 'mb-2'
                    )}
                  >
                    <Text
                      copyText={item.pointDetailsNoOfPoints}
                      fontSize={Types.size.medium}
                      element={Types.tags.span}
                      customClass={clsx(
                        'mr-2 mr-md-3 mb-1 mb-md-0',
                        'points-earned-number',
                        cardType === COBRAND_CARD_TYPE.BUSINESS ? 'text-center' : 'text-md-center text-left'
                      )}
                    />
                    <Text
                      copyText={item.pointDetailsDescription}
                      fontSize={Types.size.extraSmall}
                      element={Types.tags.span}
                      customClass={clsx('align-self-baseline align-self-md-center')}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </StyledPointsEarned>
  );
});

// Display name for debugging
PointsEarnedComp.displayName = 'PointsEarnedComp';

export const PointsEarned: FC<PointsEarnedProps> = props => {
  return (
    <EditableComponent config={PointsEarnedConfig} {...props}>
      <PointsEarnedComp {...props} />
    </EditableComponent>
  );
};
